import { createSelector } from 'reselect';
// Import state types.
import {
	AppState,
	NewBusinessApplicationPreliminaryState,
} from '@app/redux/states';
// Import api models.
import {
	AddressLocationAsMailingAddress,
	PerilDeductible,
	PossiblePeril,
} from '@app/models/api';
// Import props.
import {
	SectionProps
} from '@app/props';
// Import selectors.
import {
	makeGetAllowedPossiblePerilsSelector,
} from '@app/redux/selectors';
// Import enumerations.
import {
	NewBusinessApplicationSectionName,
	Peril,
} from '@app/enumerations';


// Fetches the new business application preliminary state.
const getNewBusinessApplicationPreliminaryState = (state: AppState, props: SectionProps<NewBusinessApplicationSectionName>): NewBusinessApplicationPreliminaryState | undefined => {
	return state.newBusinessApplicationPreliminary;
}

// Fetches the new business application preliminary state.
const getNewBusinessApplicationPreliminarySectionName = (state: AppState, props: SectionProps<NewBusinessApplicationSectionName>): NewBusinessApplicationSectionName => {
	return props.sectionName;
}

// Get New Business Application Preliminary Perils Deductibles Selector.
const makeGetNewBusinessApplicationPreliminaryPerilsDeductiblesSelector = () => createSelector(
	[getNewBusinessApplicationPreliminaryState],
	(newBusinessApplicationPreliminaryState: NewBusinessApplicationPreliminaryState | undefined): PerilDeductible[] => {
		return newBusinessApplicationPreliminaryState !== undefined
			? newBusinessApplicationPreliminaryState.perilsDeductibles
			: [];
	}
);

// Get New Business Application Preliminary Peril Codes Selector.
const makeGetNewBusinessApplicationPreliminaryPerilCodesSelector = () => createSelector(
	[makeGetNewBusinessApplicationPreliminaryPerilsDeductiblesSelector()],
	(perilsDeductibles: PerilDeductible[]): Peril[] => {
		return perilsDeductibles
			.map((perilDeductible: PerilDeductible) => perilDeductible.perilCode !== null ? perilDeductible.perilCode : Peril.NONE)
			.filter((perilCode: Peril) => perilCode !== Peril.NONE);
	}
);

// Get New Business Application Preliminary Property Address Selector.
const makeGetNewBusinessApplicationPreliminaryPropertyAddressSelector = () => createSelector(
	[getNewBusinessApplicationPreliminaryState],
	(newBusinessApplicationPreliminaryState: NewBusinessApplicationPreliminaryState | undefined): AddressLocationAsMailingAddress | undefined => {
		return newBusinessApplicationPreliminaryState !== undefined
			? newBusinessApplicationPreliminaryState.propertyAddress
			: undefined;
	}
);

// Get New Business Application Preliminary Property Address State Selector.
const makeGetNewBusinessApplicationPreliminaryPropertyAddressStateSelector = () => createSelector(
	[makeGetNewBusinessApplicationPreliminaryPropertyAddressSelector()],
	(addressLocationAsMailingAddress: AddressLocationAsMailingAddress | undefined): string | undefined => {
		return addressLocationAsMailingAddress !== undefined
			&& addressLocationAsMailingAddress.address !== null
			&& addressLocationAsMailingAddress.address.state !== null
			? addressLocationAsMailingAddress.address.state
			: undefined;
	}
);

// Is New Business Application Preliminary Property Address Complete Selector.
const makeIsNewBusinessApplicationPrelminaryPropertyAddressCompleteSelector = () => createSelector(
	[makeGetNewBusinessApplicationPreliminaryPropertyAddressSelector()],
	(propertyAddress: AddressLocationAsMailingAddress | undefined): boolean => {
		return propertyAddress !== undefined && propertyAddress.copyToMailingAddress !== null;
	}
)

// Are New Business Application Preliminary Perils Complete Selector.
const makeAreNewBusinessApplicationPreliminaryPerilsCompleteSelector = () => createSelector(
	[makeGetNewBusinessApplicationPreliminaryPerilCodesSelector(), makeGetAllowedPossiblePerilsSelector()],
	(selectedPerils: Peril[], allowedPossiblePerils: PossiblePeril[]): boolean => {
		return selectedPerils.length > 0
			&& selectedPerils.every((selectedPeril: Peril) => {
				return allowedPossiblePerils.find((possiblePeril: PossiblePeril) => possiblePeril.code === selectedPeril) !== undefined
			})
	}
);

// Is New Business Application Preliminary Section Accessible Selector.
const makeIsNewBusinessApplicationPreliminarySectionAccessibleSelector = () => createSelector(
	[makeIsNewBusinessApplicationPrelminaryPropertyAddressCompleteSelector(), makeAreNewBusinessApplicationPreliminaryPerilsCompleteSelector(), getNewBusinessApplicationPreliminarySectionName],
	(isPropertyAddressComplete: boolean, arePreliminaryPerilsComplete: boolean, sectionName: NewBusinessApplicationSectionName): boolean => {
		let isAccessible: boolean = false;
		switch (sectionName) {
			case NewBusinessApplicationSectionName.PROPERTY_ADDRESS:
				isAccessible = true;
				break;
			case NewBusinessApplicationSectionName.PERILS:
				isAccessible = isPropertyAddressComplete;
				break;
			case NewBusinessApplicationSectionName.APPETITE_AND_ELIGIBILITY:
				isAccessible = isPropertyAddressComplete && arePreliminaryPerilsComplete;
				break;
		}

		return isAccessible;
	}
);

// Is New Business Application Preliminary Section Complete Selector.
const makeIsNewBusinessApplicationPreliminarySectionCompleteSelector = () => createSelector(
	[makeIsNewBusinessApplicationPrelminaryPropertyAddressCompleteSelector(), makeAreNewBusinessApplicationPreliminaryPerilsCompleteSelector(), getNewBusinessApplicationPreliminarySectionName],
	(isPropertyAddressComplete: boolean, arePreliminaryPerilsComplete: boolean, sectionName: NewBusinessApplicationSectionName): boolean => {
		let isComplete: boolean = false;
		switch (sectionName) {
			case NewBusinessApplicationSectionName.PROPERTY_ADDRESS:
				isComplete = isPropertyAddressComplete;
				break;
			case NewBusinessApplicationSectionName.PERILS:
				isComplete = arePreliminaryPerilsComplete;
				break;
		}

		return isComplete;
	}
);


// Export composed methods.
export {
	getNewBusinessApplicationPreliminaryState,
};

// Export selectors.
export {
	makeGetNewBusinessApplicationPreliminaryPerilsDeductiblesSelector,
	makeGetNewBusinessApplicationPreliminaryPerilCodesSelector,
	makeGetNewBusinessApplicationPreliminaryPropertyAddressSelector,
	makeGetNewBusinessApplicationPreliminaryPropertyAddressStateSelector,
	makeIsNewBusinessApplicationPrelminaryPropertyAddressCompleteSelector,
	makeAreNewBusinessApplicationPreliminaryPerilsCompleteSelector,
	makeIsNewBusinessApplicationPreliminarySectionAccessibleSelector,
	makeIsNewBusinessApplicationPreliminarySectionCompleteSelector,
};